/**
 * LIVISIBLY DESIGN SYSTEM — MUI THEME OVERRIDE
 *
 * Maps our design tokens onto MUI's createTheme so that
 * existing MUI components (DataGrid, DatePickers, etc.)
 * automatically inherit the new visual language.
 *
 * New components should use CSS custom properties directly.
 * This bridge exists for MUI compatibility during migration.
 */

import { createTheme } from '@mui/material/styles';
import { colors, typography, shadows, radii } from '../tokens/tokens';

const livisiblyTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.navy[700],
      light: colors.navy[400],
      dark: colors.navy[900],
      contrastText: colors.white,
    },
    secondary: {
      main: colors.accent[500],
      light: colors.accent[300],
      dark: colors.accent[700],
      contrastText: colors.white,
    },
    error: {
      main: colors.error[500],
      light: colors.error[100],
      dark: colors.error[700],
    },
    warning: {
      main: colors.warning[500],
      light: colors.warning[100],
      dark: colors.warning[700],
    },
    success: {
      main: colors.success[500],
      light: colors.success[100],
      dark: colors.success[700],
    },
    info: {
      main: colors.info[500],
      light: colors.info[100],
      dark: colors.info[700],
    },
    background: {
      default: colors.gray[50],
      paper: colors.white,
    },
    text: {
      primary: colors.gray[900],
      secondary: colors.gray[600],
      disabled: colors.gray[400],
    },
    divider: colors.gray[200],
    grey: colors.gray,
  },

  typography: {
    fontFamily: typography.fontFamily.body,
    fontSize: 15, // base size in px (MUI converts to rem)
    h1: {
      fontFamily: typography.fontFamily.display,
      fontWeight: typography.fontWeight.bold,
      fontSize: typography.fontSize['4xl'],
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: typography.fontFamily.display,
      fontWeight: typography.fontWeight.semibold,
      fontSize: typography.fontSize['3xl'],
      lineHeight: 1.2,
    },
    h3: {
      fontFamily: typography.fontFamily.display,
      fontWeight: typography.fontWeight.semibold,
      fontSize: typography.fontSize['2xl'],
      lineHeight: 1.3,
    },
    h4: {
      fontFamily: typography.fontFamily.display,
      fontWeight: typography.fontWeight.semibold,
      fontSize: typography.fontSize.xl,
      lineHeight: 1.35,
    },
    h5: {
      fontFamily: typography.fontFamily.display,
      fontWeight: typography.fontWeight.semibold,
      fontSize: typography.fontSize.lg,
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: typography.fontFamily.display,
      fontWeight: typography.fontWeight.semibold,
      fontSize: typography.fontSize.base,
      lineHeight: 1.5,
    },
    body1: {
      fontFamily: typography.fontFamily.body,
      fontSize: typography.fontSize.base,
      lineHeight: 1.5,
    },
    body2: {
      fontFamily: typography.fontFamily.body,
      fontSize: typography.fontSize.sm,
      lineHeight: 1.5,
    },
    caption: {
      fontFamily: typography.fontFamily.body,
      fontSize: typography.fontSize.xs,
      lineHeight: 1.5,
      color: colors.gray[500],
    },
    button: {
      fontFamily: typography.fontFamily.body,
      fontWeight: typography.fontWeight.medium,
      fontSize: typography.fontSize.sm,
      textTransform: 'none', // no ALL CAPS buttons
    },
    overline: {
      fontFamily: typography.fontFamily.body,
      fontSize: '0.6875rem',
      fontWeight: typography.fontWeight.semibold,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    },
  },

  shape: {
    borderRadius: radii.md,
  },

  shadows: [
    'none',
    shadows.sm,
    shadows.sm,
    shadows.md,
    shadows.md,
    shadows.md,
    shadows.lg,
    shadows.lg,
    shadows.lg,
    shadows.lg,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    ...Array(12).fill(shadows.xl),
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: typography.fontFamily.body,
          fontSize: typography.fontSize.base,
          backgroundColor: colors.gray[50],
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          padding: '8px 20px',
          fontWeight: typography.fontWeight.medium,
          minHeight: 40,
        },
        contained: {
          '&:hover': {
            boxShadow: shadows.md,
          },
        },
        outlined: {
          borderColor: colors.gray[300],
          '&:hover': {
            borderColor: colors.gray[400],
            backgroundColor: colors.gray[50],
          },
        },
      },
    },

    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: radii.lg,
          border: `1px solid ${colors.gray[200]}`,
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radii.lg,
          border: `1px solid ${colors.gray[200]}`,
          boxShadow: shadows.sm,
          '&:hover': {
            boxShadow: shadows.md,
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radii.sm,
          fontWeight: typography.fontWeight.medium,
          fontSize: typography.fontSize.xs,
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: radii.md,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.gray[400],
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.navy[400],
              borderWidth: 2,
            },
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radii.xl,
          boxShadow: shadows.xl,
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily.display,
          fontWeight: typography.fontWeight.semibold,
          fontSize: typography.fontSize.xl,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: typography.fontWeight.semibold,
            fontSize: typography.fontSize.xs,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: colors.gray[600],
            backgroundColor: colors.gray[50],
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: typography.fontSize.sm,
          borderBottom: `1px solid ${colors.gray[100]}`,
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${colors.gray[200]}`,
          borderRadius: radii.lg,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.gray[50],
            borderBottom: `1px solid ${colors.gray[200]}`,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: typography.fontWeight.semibold,
            fontSize: typography.fontSize.xs,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          },
          '& .MuiDataGrid-cell': {
            fontSize: typography.fontSize.sm,
            borderBottom: `1px solid ${colors.gray[100]}`,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: colors.gray[50],
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.gray[900],
          fontSize: typography.fontSize.xs,
          fontWeight: typography.fontWeight.medium,
          borderRadius: radii.sm,
          padding: '4px 12px',
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          color: colors.gray[900],
          boxShadow: 'none',
          borderBottom: `1px solid ${colors.gray[200]}`,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.navy[900],
          borderRight: '1px solid rgba(255, 255, 255, 0.06)',
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          margin: '2px 8px',
          padding: '8px 12px',
          minHeight: 44,
          '&.Mui-selected': {
            backgroundColor: colors.navy[700],
            '&:hover': {
              backgroundColor: colors.navy[600],
            },
          },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: typography.fontWeight.medium,
          fontSize: typography.fontSize.sm,
          minHeight: 44,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          fontSize: typography.fontSize.sm,
        },
      },
    },
  },
});

export default livisiblyTheme;
